import type { NearbyAttraction } from '@/entities/attraction';
import type { AttractionCardResponse, AttractionSummary } from '@/shared/api/experience';
import type { ExperiencePhoto } from '@/widgets/photosPreview/types/experience';

import type { AttractionCard } from '../models/attractionCard';

const ATTRACTIONS_BREADCRUMB_PATH = '/attractions/';

/** Листинг экскурсий города: слаг города — второй сегмент ссылки на листинг достопримечательности. */
export const toCityExperiencesUrl = (experiencesUrl = ''): string => {
  const citySlug = experiencesUrl.split('/').filter(Boolean)[1];

  return citySlug ? `/experience/${citySlug}/` : '';
};

const toNearbyAttraction = (item: AttractionSummary): NearbyAttraction => ({
  id: item.id ?? 0,
  name: item.name ?? '',
  slug: item.slug,
  description: item.description,
  address: item.address,
  excursions_count: item.excursions_count,
  latitude: item.latitude,
  longitude: item.longitude,
  citytag_url: item.slug ? `/sights/${item.id}-${item.slug}/` : undefined,
  cover_photo: {
    thumbnail: item.photo_url,
    medium: item.photo_url,
  },
  experience_main_photo_url: item.photo_url,
});

export const mapAttractionCard = (response: AttractionCardResponse): AttractionCard => {
  const attraction = response.attraction ?? {};
  const canonicalUrl = response.metatags?.canonical_url ?? '';

  const photos: ExperiencePhoto[] = (response.photos ?? []).map((photo, index) => ({
    // в схеме BFF у фото нет id, для согу берём индекс, пока бэкенд его не пришлёт
    id: 'id' in photo && typeof photo.id === 'number' ? photo.id : index,
    medium: photo.medium,
    thumbnail: photo.thumbnail,
    type: 'photo',
  }));

  const hasCoordinates = attraction.latitude != null && attraction.longitude != null;

  return {
    id: attraction.id ?? 0,
    slug: attraction.slug ?? '',
    // в metatags.h1 лежит SEO-заголовок листинга («Экскурсии в дом Бенуа»), карточке нужно имя места
    name: attraction.name || response.metatags?.h1 || '',
    description: attraction.description ?? '',
    // приходит с HTML-сущностями («в&nbsp;Санкт‑Петербурге»), выводится через v-html
    articleTitle: (response.article_title ?? '').trim(),

    // у крошки текущей страницы адреса нет, пустой `to` оставляет её без ссылки
    breadcrumbs: (response.breadcrumbs ?? []).flatMap(({ name, url }) =>
      name ? [{ label: name, to: url ?? '' }] : [],
    ),

    photos,
    galleryExperience: {
      id: attraction.id ?? 0,
      title: attraction.name ?? '',
      url: canonicalUrl,
      photos,
    },

    info: {
      address: attraction.address,
    },

    location: hasCoordinates
      ? {
          description: attraction.address,
          latitude: attraction.latitude as number,
          longitude: attraction.longitude as number,
        }
      : undefined,

    schedule: (response.schedule ?? []).map((event) => ({
      id: event.exp_id ?? 0,
      title: event.exp_title ?? '',
      url: event.exp_url ?? '',
      startsAt: event.event_dt,
      priceText: event.price_text,
    })),

    experiences: {
      count: attraction.excursions_count ?? response.related_experiences?.count ?? 0,
      url: response.experiences_url ?? '',
      cityUrl: toCityExperiencesUrl(response.experiences_url),
    },

    nearby: {
      // «в Санкт-Петербурге» — подставляется в заголовок блока на странице
      cityPhrase: response.city_in_obj_phrase,
      url: response.breadcrumbs?.find(({ url }) => url?.includes(ATTRACTIONS_BREADCRUMB_PATH))?.url ?? '',
      items: (response.related_attractions ?? []).map(toNearbyAttraction),
    },

    faq: {
      title: response.faq?.title ?? '',
      items: (response.faq?.items ?? []).map((item) => ({
        question: item.question ?? '',
        answerHtml: item.answer ?? '',
      })),
    },
  };
};
